import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { emailInvalidReason } from "@/lib/emailAudit";
import { Plus, Search, Upload as UploadIcon, ArrowRight, Trash2, Pencil, FileSpreadsheet, Tag, X, Download } from "lucide-react";

const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  responded: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_interested: "bg-zinc-50 text-zinc-700 border-zinc-200",
  bounced: "bg-rose-50 text-rose-700 border-rose-200",
  converted: "bg-primary/10 text-primary border-primary/30",
};

const SOURCES = ["cold_list", "linkedin", "event", "exhibition", "webinar", "website_form", "referral", "purchased_list", "newsletter", "import", "visiting_card", "other"];

const BLANK = { name: "", designation: "", company_name: "", email: "", phone: "", website: "", state: "", district: "", country: "", industry: "", categories: [], source: "other", status: "new", notes: "" };

function AddCategoryDialog({ open, onOpenChange, onAdded, editing }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setName(editing?.name || ""); }, [open, editing]);
  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Enter a category name"); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        const { data } = await api.patch(`/marketing-contact-categories/${editing.id}`, { name: n });
        toast.success(`Renamed to "${data.name}"${data.contacts_updated ? ` — ${data.contacts_updated} contact(s) updated` : ""}`);
        onOpenChange(false);
        onAdded?.(data);
      } else {
        const { data } = await api.post("/marketing-contact-categories", { name: n });
        toast.success(`Category "${data.name}" added`);
        setName("");
        onOpenChange(false);
        onAdded?.(data);
      }
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Tag className="h-5 w-5 text-primary" /> {editing?.id ? "Rename category" : "New category"}</DialogTitle>
          <DialogDescription>{editing?.id ? "All contacts in this category move with it." : "e.g. Automotive, Mining, Textile — visible to the whole team."}</DialogDescription>
        </DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name" className="rounded-2xl h-11"
          onKeyDown={(e) => e.key === "Enter" && save()} data-testid="mkt-category-name-input" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-2xl tap" onClick={save} disabled={saving} data-testid="mkt-category-save">{saving ? "Saving…" : editing?.id ? "Rename" : "Add category"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactDialog({ open, onOpenChange, contact, categories, onDone }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const cats = contact?.categories || (contact?.category ? [contact.category] : []);
      setForm(contact?.id ? { ...BLANK, ...contact, categories: cats } : { ...BLANK, categories: cats });
    }
  }, [open, contact]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (name) => setForm((f) => {
    const cur = f.categories || [];
    return { ...f, categories: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
  });
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, categories: form.categories || [], category: null };
      if (contact?.id) {
        await api.patch(`/marketing-contacts/${contact.id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/marketing-contacts", payload);
        toast.success("Marketing contact added");
      }
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{contact?.id ? "Edit marketing contact" : "New marketing contact"}</DialogTitle>
          <DialogDescription>Lives only in Marketing Contacts — never mixed into CRM contacts.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold">Name *</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="mkt-contact-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Designation</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} data-testid="mkt-contact-designation" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Company</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="mkt-contact-company" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold">Categories</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="mkt-contact-categories">
              {categories.length === 0 && <p className="text-xs text-muted-foreground">No categories yet — add one from the tabs above.</p>}
              {categories.map((c) => {
                const on = (form.categories || []).includes(c.name);
                return (
                  <button key={c.id} type="button" onClick={() => toggleCat(c.name)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border/70 hover:border-primary/50"
                    }`}
                    data-testid={`mkt-contact-cat-toggle-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {on && <span aria-hidden>✓</span>} {c.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">A contact can belong to several categories at the same time.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Email</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="mkt-contact-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Phone</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="mkt-contact-phone" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Website</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="www.company.com" data-testid="mkt-contact-website" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Industry</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} data-testid="mkt-contact-industry" />
          </div>
          <div>
            <Label className="text-xs font-semibold">City / District</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.district || ""} onChange={(e) => set("district", e.target.value)} data-testid="mkt-contact-city" />
          </div>
          <div>
            <Label className="text-xs font-semibold">State</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.state || ""} onChange={(e) => set("state", e.target.value)} data-testid="mkt-contact-state" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Country</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.country || ""} onChange={(e) => set("country", e.target.value)} placeholder="India" data-testid="mkt-contact-country" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Source</Label>
            <Select value={form.source || "other"} onValueChange={(v) => set("source", v)}>
              <SelectTrigger className="rounded-2xl h-10 mt-1" data-testid="mkt-contact-source"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea rows={2} className="rounded-2xl mt-1" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="mkt-contact-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-2xl tap" onClick={save} disabled={saving} data-testid="mkt-contact-save">{saving ? "Saving…" : "Save contact"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Header aliases → canonical marketing-contact field names.
const HEADER_MAP = {
  name: "name", "full name": "name", "contact name": "name", "contact": "name",
  designation: "designation", title: "designation", role: "designation",
  company: "company_name", "company name": "company_name", company_name: "company_name", organisation: "company_name", organization: "company_name",
  email: "email", "e-mail": "email", "email id": "email", mail: "email",
  phone: "phone", mobile: "phone", "phone number": "phone", "mobile number": "phone", contact_number: "phone", "contact no": "phone",
  linkedin: "linkedin_url", linkedin_url: "linkedin_url", "linkedin profile": "linkedin_url",
  website: "website", "web site": "website", url: "website",
  country: "country",
  industry: "industry", location: "location", city: "district", district: "district", state: "state",
  source: "source", notes: "notes", remarks: "notes", category: "category", segment: "category",
};

// 24.docx: NEVER drop rows while parsing — every uploaded record must be
// accounted for. Classification happens at import time with exact reasons.
function normalizeRows(rawRows) {
  return rawRows.map((raw) => {
    const row = {};
    Object.entries(raw).forEach(([k, v]) => {
      const key = HEADER_MAP[String(k).trim().toLowerCase()];
      const val = String(v ?? "").trim();
      if (key && val) row[key] = val;
    });
    return row;
  });
}

const REPORT_BUCKETS = [
  { key: "imported", label: "Successfully imported", cls: "text-emerald-700" },
  { key: "existing", label: "Already existing records", cls: "text-blue-700" },
  { key: "duplicates", label: "Duplicates (within file)", cls: "text-amber-600" },
  { key: "invalid", label: "Invalid emails", cls: "text-red-600" },
  { key: "empty", label: "Empty email records", cls: "text-zinc-600" },
  { key: "rejected", label: "Rejected / failed records", cls: "text-rose-700" },
];

function downloadReportRows(label, rowsArr) {
  const data = rowsArr.map((r) => ({
    Name: r.name || "", Email: r.email || "", Phone: r.phone || "", Company: r.company_name || "",
    Status: r.status_label || label, Reason: r.reason || "",
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 30));
  XLSX.writeFile(wb, `import-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function ImportDialog({ open, onOpenChange, categories, defaultCategory, onDone }) {
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [detailKey, setDetailKey] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setCategory(defaultCategory || ""); setRows([]); setFileName(""); setReport(null); setDetailKey(null); } }, [open, defaultCategory]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const parsed = normalizeRows(json);
      if (parsed.length === 0) { toast.error("No rows found in the file. Needs columns like: name, email, phone, company"); return; }
      setRows(parsed);
      setFileName(file.name);
      setReport(null);
    } catch (err) {
      toast.error("Could not read the file — use .xlsx, .xls or .csv");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const importNow = async () => {
    if (rows.length === 0) { toast.error("Upload a file first"); return; }
    setRunning(true);
    try {
      // Phase 1 — classify EVERY record locally (24.docx: nothing disappears).
      const rpt = { total: rows.length, imported: [], existing: [], duplicates: [], invalid: [], empty: [], rejected: [] };
      const seen = new Set();
      const toSend = [];
      for (const row of rows) {
        const email = (row.email || "").trim();
        if (!email) {
          rpt.empty.push({ ...row, reason: (row.name || row.phone) ? "No email address in this row" : "Row has no email, name or phone" });
          continue;
        }
        const reason = emailInvalidReason(email);
        if (reason) { rpt.invalid.push({ ...row, reason }); continue; }
        const lc = email.toLowerCase();
        if (seen.has(lc)) { rpt.duplicates.push({ ...row, reason: "Duplicate of another row in this file" }); continue; }
        seen.add(lc);
        toSend.push({ ...row, email });
      }
      // Phase 2 — send clean rows; backend reports inserted / existing / failed per email.
      for (let i = 0; i < toSend.length; i += 2000) {
        const chunk = toSend.slice(i, i + 2000);
        const { data } = await api.post("/marketing-contacts/bulk-import", { contacts: chunk, category: category || undefined });
        const updatedSet = new Set((data.updated_emails || []).map((e) => (e || "").toLowerCase()));
        const skippedMap = new Map((data.skipped_rows || []).map((r) => [(r.email || "").toLowerCase(), r.reason]));
        const errMap = new Map((data.errors || []).map((r) => [(r.email || "").toLowerCase(), r.error]));
        for (const row of chunk) {
          const lc = row.email.toLowerCase();
          if (updatedSet.has(lc)) {
            rpt.existing.push({ ...row, reason: category ? `Already in Marketing Contacts — added to ${category}` : "Already exists in Marketing Contacts" });
          } else if (skippedMap.has(lc)) {
            const reason = skippedMap.get(lc) || "Skipped by server";
            (reason.startsWith("Already") ? rpt.existing : rpt.rejected).push({ ...row, reason });
          } else if (errMap.has(lc)) {
            rpt.rejected.push({ ...row, reason: errMap.get(lc) || "Server error" });
          } else {
            rpt.imported.push({ ...row, reason: "" });
          }
        }
      }
      setReport(rpt);
      onDone?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setRunning(false); }
  };

  const accounted = report ? REPORT_BUCKETS.reduce((s, b) => s + report[b.key].length, 0) : 0;

  const downloadFullReport = () => {
    const all = REPORT_BUCKETS.flatMap((b) => report[b.key].map((r) => ({ ...r, status_label: b.label })));
    downloadReportRows("Complete Import Report", all);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> {report ? "Import complete" : "Import marketing contacts"}</DialogTitle>
          {!report && (
            <DialogDescription>
              Upload Excel (.xlsx) or CSV. Contacts are saved ONLY into Marketing Contacts — never into CRM Contacts, Leads or Clients.
              Recognised columns: name, designation, company, email, phone, linkedin, industry, state, city, notes, category.
            </DialogDescription>
          )}
        </DialogHeader>
        {report ? (
          <div className="space-y-3" data-testid="mkt-import-summary">
            <div className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3">
              <p className="text-sm font-bold" data-testid="mkt-import-summary-total">{report.total.toLocaleString()} records processed from <em>{fileName}</em></p>
              <div className="mt-2 space-y-1">
                {REPORT_BUCKETS.map((b) => (
                  <div key={b.key} className="flex items-center justify-between text-xs">
                    <button type="button" onClick={() => setDetailKey(detailKey === b.key ? null : b.key)}
                      className={`underline-offset-2 ${report[b.key].length ? "underline font-semibold " + b.cls : "text-muted-foreground cursor-default"}`}
                      data-testid={`mkt-import-bucket-${b.key}`}>
                      {b.label}
                    </button>
                    <span className={`font-bold ${b.cls}`} data-testid={`mkt-import-count-${b.key}`}>{report[b.key].length.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                  <span className="font-bold">Total accounted records</span>
                  <span className={`font-bold ${accounted === report.total ? "text-emerald-700" : "text-red-600"}`} data-testid="mkt-import-accounted">{accounted.toLocaleString()} / {report.total.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <Button variant="outline" className="rounded-2xl w-full" onClick={() => setDetailKey(detailKey ? null : "imported")} data-testid="mkt-import-details-toggle">
              {detailKey ? "Hide Import Details" : "View Import Details"}
            </Button>
            {detailKey && (
              <div className="rounded-2xl border border-border/60 overflow-hidden" data-testid="mkt-import-detail-panel">
                <div className="flex flex-wrap gap-1 p-2 bg-muted/30 border-b border-border/50">
                  {REPORT_BUCKETS.map((b) => (
                    <button key={b.key} type="button" onClick={() => setDetailKey(b.key)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${detailKey === b.key ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70"}`}
                      data-testid={`mkt-import-detail-tab-${b.key}`}>
                      {b.label} ({report[b.key].length})
                    </button>
                  ))}
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-border/40">
                  {report[detailKey].length === 0 ? (
                    <p className="text-xs text-muted-foreground p-3">No records in this category.</p>
                  ) : report[detailKey].slice(0, 500).map((r, i) => (
                    <div key={i} className="px-3 py-1.5 text-xs flex items-start justify-between gap-2">
                      <span className="font-mono truncate">{r.email || r.name || r.phone || "(blank row)"}</span>
                      {r.reason && <span className="text-muted-foreground text-[10px] shrink-0 max-w-[45%] text-right">{r.reason}</span>}
                    </div>
                  ))}
                  {report[detailKey].length > 500 && <p className="text-[10px] text-muted-foreground p-2">Showing first 500 — download the list for everything.</p>}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "Valid Emails", key: "imported", tid: "valid" },
                { label: "Duplicate Emails", key: "duplicates", tid: "duplicates" },
                { label: "Invalid Emails", key: "invalid", tid: "invalid" },
                { label: "Rejected/Failed", key: "rejected", tid: "rejected" },
              ].map((d) => (
                <Button key={d.key} type="button" variant="outline" size="sm" className="rounded-xl h-8 text-[11px]" disabled={report[d.key].length === 0}
                  onClick={() => downloadReportRows(d.label, report[d.key])} data-testid={`mkt-import-dl-${d.tid}`}>
                  <Download className="h-3 w-3 mr-1" /> {d.label} ({report[d.key].length})
                </Button>
              ))}
              <Button type="button" size="sm" className="rounded-xl h-8 text-[11px]" onClick={downloadFullReport} data-testid="mkt-import-dl-report">
                <Download className="h-3 w-3 mr-1" /> Complete Import Report
              </Button>
            </div>
            <div className="flex justify-end">
              <Button className="rounded-2xl" onClick={() => onOpenChange(false)} data-testid="mkt-import-close">Done</Button>
            </div>
          </div>
        ) : (
        <>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Category / Segment for this upload</Label>
            <Select value={category || "__none"} onValueChange={(v) => setCategory(v === "__none" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-11 mt-1" data-testid="mkt-import-category"><SelectValue placeholder="— No category —" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— No category —</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Every imported contact is saved into exactly this category. When one is selected here, any "category" column inside the file is ignored.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">File</Label>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile}
              className="mt-1 block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-primary file:font-semibold file:cursor-pointer cursor-pointer rounded-2xl border border-border/60 p-1.5"
              data-testid="mkt-import-file" />
            {rows.length > 0 && (
              <div className="mt-2 rounded-2xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="mkt-import-preview">
                <strong>{rows.length}</strong> record{rows.length === 1 ? "" : "s"} found in <em>{fileName}</em> — every one will be accounted for in the import summary.
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-2xl tap" onClick={importNow} disabled={running || rows.length === 0} data-testid="mkt-import-run">
            {running ? "Importing…" : <><UploadIcon className="h-4 w-4 mr-1.5" /> Import {rows.length > 0 ? rows.length : ""}</>}
          </Button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarketingContactsPanel() {
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || (user?.permissions || []).includes("add_marketing");
  const canDelete = user?.role === "admin" || (user?.permissions || []).includes("delete_marketing");
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openImport, setOpenImport] = useState(false);
  const [openAddCat, setOpenAddCat] = useState(false);
  const [renamingCat, setRenamingCat] = useState(null);

  const loadCategories = useCallback(async () => {
    try { const { data } = await api.get("/marketing-contact-categories"); setCategories(data); } catch { /* noop */ }
  }, []);

  const PAGE = 200;

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    setSelectAllMatching(false);
    try {
      const { data } = await api.get("/marketing-contacts", {
        params: { q: q || undefined, category: activeCat || undefined, paginated: 1, skip: 0, limit: PAGE },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, activeCat]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/marketing-contacts", {
        params: { q: q || undefined, category: activeCat || undefined, paginated: 1, skip: rows.length, limit: PAGE },
      });
      setRows((r) => [...r, ...(data.rows || [])]);
      setTotal(data.total || 0);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const convertToLead = async (c) => {
    if (!window.confirm(`Convert "${c.name}" into a Lead in the sales pipeline?`)) return;
    try {
      await api.post(`/marketing-contacts/${c.id}/convert-to-lead`);
      toast.success("Converted to lead — find them in Leads");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive "${c.name}"?`)) return;
    try { await api.delete(`/marketing-contacts/${c.id}`); toast.success("Archived"); load(); } catch (err) { toast.error(fmtError(err)); }
  };

  const removeFromCategory = async (c, cat) => {
    if (!window.confirm(`Remove "${c.name}" from "${cat}"?\n\nThey stay in All Marketing Contacts and any other categories.`)) return;
    try {
      await api.patch(`/marketing-contacts/${c.id}`, { categories: (c.categories || []).filter((x) => x !== cat) });
      toast.success(`Removed from ${cat}`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const [removingCat, setRemovingCat] = useState(false);
  const bulkRemoveFromCategory = async () => {
    if (selectedCount === 0 || !activeCat) return;
    if (!window.confirm(`Remove ${selectedCount.toLocaleString()} contact${selectedCount === 1 ? "" : "s"} from "${activeCat}"?\n\nThey are NOT deleted — they stay in All Marketing Contacts and any other categories.`)) return;
    setRemovingCat(true);
    try {
      const payload = selectAllMatching
        ? { all_matching: true, category: activeCat, q: q || undefined }
        : { ids: [...selected], category: activeCat };
      const { data } = await api.post("/marketing-contacts/bulk-remove-category", payload);
      toast.success(`Removed ${data.removed.toLocaleString()} contact${data.removed === 1 ? "" : "s"} from ${activeCat}`);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setRemovingCat(false); }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?\n\nIt will also be removed from every contact tagged with it — contacts stay in All Marketing Contacts and their other categories.`)) return;
    try {
      const { data } = await api.delete(`/marketing-contact-categories/${cat.id}`);
      toast.success(`Category "${cat.name}" deleted${data.contacts_untagged ? ` — removed from ${data.contacts_untagged} contact${data.contacts_untagged === 1 ? "" : "s"}` : ""}`);
      if (activeCat === cat.name) setActiveCat("");
      loadCategories();
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  // Export ALL matching contacts (not just the loaded page) — respects the
  // active category tab and search. Marketing DB only (11.docx).
  const exportContacts = async () => {
    let allRows = [];
    try {
      const { data } = await api.get("/marketing-contacts", {
        params: { q: q || undefined, category: activeCat || undefined },
      });
      allRows = data || [];
    } catch { toast.error("Could not fetch contacts for export"); return; }
    if (allRows.length === 0) { toast.info(activeCat ? `No contacts in "${activeCat}" to export` : "No contacts to export"); return; }
    const data = allRows.map((c) => ({
      Name: c.name || "",
      Designation: c.designation || "",
      Company: c.company_name || "",
      Email: c.email || "",
      Phone: c.phone || "",
      Category: (c.categories || []).join(", "),
      Industry: c.industry || "",
      Website: c.website || "",
      City: c.district || "",
      State: c.state || "",
      Country: c.country || "",
      LinkedIn: c.linkedin_url || "",
      Source: c.source || "",
      Status: c.status || "",
      Notes: c.notes || "",
      "Created By": c.created_by_name || "",
      "Created Date": c.created_at || "",
      "Converted By": c.converted_by_name || "",
      "Converted Date": c.converted_at || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeCat ? activeCat.slice(0, 30) : "All Marketing Contacts");
    const slug = activeCat ? activeCat.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all";
    XLSX.writeFile(wb, `marketing-contacts-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Exported ${allRows.length} contact${allRows.length === 1 ? "" : "s"}${activeCat ? ` from ${activeCat}` : ""}`);
  };

  const selectedCount = selectAllMatching ? total : selected.size;
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const toggleRow = (id) => {
    setSelectAllMatching(false);
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleSelectAllVisible = () => {
    setSelectAllMatching(false);
    setSelected(allVisibleSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };

  const bulkDelete = async () => {
    if (selectedCount === 0) return;
    const where = activeCat ? ` from ${activeCat}` : "";
    if (!window.confirm(`Delete ${selectedCount.toLocaleString()} Marketing Contact${selectedCount === 1 ? "" : "s"}${where}?\n\nThis action will permanently remove all selected contacts from Marketing Contacts. CRM Contacts, Leads, Clients and Accounts are NOT affected.`)) return;
    setDeleting(true);
    try {
      const payload = selectAllMatching
        ? { all_matching: true, category: activeCat || undefined, q: q || undefined }
        : { ids: [...selected] };
      const { data } = await api.post("/marketing-contacts/bulk-delete", payload);
      toast.success(`Deleted ${data.deleted.toLocaleString()} marketing contact${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setDeleting(false); }
  };

  const catTabs = useMemo(() => [{ id: "__all", name: "All Marketing Contacts" }, ...categories], [categories]);

  return (
    <div className="space-y-4" data-testid="marketing-contacts-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-2xl h-11 pl-10" placeholder="Search name, company, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mkt-contacts-search" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-2xl tap" onClick={exportContacts} data-testid="mkt-contacts-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export{activeCat ? ` (${activeCat})` : ""}
          </Button>
          {canAdd && (
            <>
              <Button variant="outline" className="rounded-2xl tap" onClick={() => setOpenImport(true)} data-testid="mkt-contacts-import-btn">
                <UploadIcon className="h-4 w-4 mr-1.5" /> Upload Contacts
              </Button>
              <Button className="rounded-2xl tap" onClick={() => setOpenNew(true)} data-testid="mkt-contacts-add-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Add contact
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="mkt-category-tabs">
        {catTabs.map((c) => {
          const active = (c.id === "__all" && !activeCat) || activeCat === c.name;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id === "__all" ? "" : c.name)}
              className={`group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                active ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border/70 hover:border-primary/50"
              }`}
              data-testid={`mkt-cat-tab-${c.id === "__all" ? "all" : c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c.name}
              {c.id !== "__all" && canAdd && (
                <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-70 hover:!opacity-100" data-testid={`mkt-cat-rename-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={(e) => { e.stopPropagation(); setRenamingCat(c); }} />
              )}
              {c.id !== "__all" && canDelete && (
                <X className="h-3 w-3 opacity-0 group-hover:opacity-70 hover:!opacity-100" onClick={(e) => { e.stopPropagation(); removeCategory(c); }} />
              )}
            </button>
          );
        })}
        {canAdd && (
          <button onClick={() => setOpenAddCat(true)}
            className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold border border-dashed border-primary/50 text-primary hover:bg-primary/5 transition-colors"
            data-testid="mkt-add-category-btn">
            <Plus className="h-3 w-3" /> Add Category
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-border/70">
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            {activeCat ? `No contacts in "${activeCat}" yet.` : "No marketing contacts yet."} Use <strong>Import</strong> to upload an Excel/CSV list or <strong>Add contact</strong>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap rounded-2xl border border-border/60 bg-muted/30 px-3.5 py-2" data-testid="mkt-selection-bar">
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
              <input type="checkbox" className="h-4 w-4 rounded accent-primary cursor-pointer" checked={allVisibleSelected} onChange={toggleSelectAllVisible} data-testid="mkt-select-all-checkbox" />
              Select All
            </label>
            <p className="text-xs text-muted-foreground" data-testid="mkt-total-count">
              <strong>{total.toLocaleString()}</strong> contact{total === 1 ? "" : "s"} in {activeCat || "All Marketing Contacts"}
              {rows.length < total ? ` · showing ${rows.length.toLocaleString()}` : ""}
            </p>
            {selectedCount > 0 && (
              <span className="text-xs font-semibold text-primary" data-testid="mkt-selected-count">{selectedCount.toLocaleString()} selected</span>
            )}
            {allVisibleSelected && !selectAllMatching && total > rows.length && (
              <button type="button" className="text-xs underline text-primary-700 font-semibold" onClick={() => setSelectAllMatching(true)} data-testid="mkt-select-all-matching">
                Select all {total.toLocaleString()} contacts{activeCat ? ` in ${activeCat}` : ""}
              </button>
            )}
            {canAdd && activeCat && selectedCount > 0 && (
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs ml-auto" disabled={removingCat} onClick={bulkRemoveFromCategory} data-testid="mkt-bulk-remove-category-btn">
                <X className="h-3.5 w-3.5 mr-1" /> {removingCat ? "Removing…" : `Remove from ${activeCat}`}
              </Button>
            )}
            {canDelete && selectedCount > 0 && (
              <Button size="sm" variant="outline" className={`rounded-xl h-8 text-xs text-rose-600 hover:text-rose-700 border-rose-200 ${activeCat ? "" : "ml-auto"}`} disabled={deleting} onClick={bulkDelete} data-testid="mkt-bulk-delete-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> {deleting ? "Deleting…" : `Delete Selected (${selectedCount.toLocaleString()})`}
              </Button>
            )}
          </div>
          {rows.map((c) => (
            <Card key={c.id} className="rounded-2xl border border-border/60 shadow-soft" data-testid={`mkt-contact-row-${c.id}`}>
              <CardContent className="p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2.5">
                <input type="checkbox" className="h-4 w-4 rounded accent-primary cursor-pointer shrink-0" checked={selectAllMatching || selected.has(c.id)} onChange={() => toggleRow(c.id)} data-testid={`mkt-contact-check-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{c.name}</p>
                    {(c.categories || (c.category ? [c.category] : [])).map((cat) => (
                      <Badge key={cat} variant="outline" className="text-[10px] rounded-full bg-violet-50 text-violet-700 border-violet-200 inline-flex items-center gap-1" data-testid={`mkt-contact-cat-badge-${c.id}-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {cat}
                        {canAdd && (
                          <X className="h-2.5 w-2.5 cursor-pointer opacity-60 hover:opacity-100" onClick={() => removeFromCategory(c, cat)} data-testid={`mkt-contact-cat-remove-${c.id}-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                        )}
                      </Badge>
                    ))}
                    <Badge variant="outline" className={`text-[10px] rounded-full capitalize ${STATUS_STYLE[c.status] || ""}`}>{c.status?.replace("_", " ")}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {[c.designation, c.company_name].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</p>
                  {(c.created_by_name || c.converted_by_name) && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5" data-testid={`mkt-contact-created-by-${c.id}`}>
                      {c.created_by_name && <>Created By: <strong className="text-foreground/70">{c.created_by_name}</strong></>}
                      {c.converted_by_name && (
                        <> · Converted By: <strong className="text-foreground/70">{c.converted_by_name}</strong>{c.converted_at ? ` (${new Date(c.converted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })})` : ""}</>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {canAdd && (
                    <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" onClick={() => setEditing(c)} data-testid={`mkt-contact-edit-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canAdd && c.status !== "converted" && (
                    <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" onClick={() => convertToLead(c)} data-testid={`mkt-contact-convert-${c.id}`}>
                      <ArrowRight className="h-3.5 w-3.5 mr-1" /> Lead
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs text-rose-600 hover:text-rose-700" onClick={() => archive(c)} data-testid={`mkt-contact-archive-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {rows.length < total && (
            <Button variant="outline" className="w-full rounded-2xl tap" disabled={loadingMore} onClick={loadMore} data-testid="mkt-load-more-btn">
              {loadingMore ? "Loading…" : `Load More (${(total - rows.length).toLocaleString()} remaining)`}
            </Button>
          )}
        </div>
      )}

      <ContactDialog open={openNew || !!editing} onOpenChange={(v) => { if (!v) { setOpenNew(false); setEditing(null); } }}
        contact={editing || (activeCat ? { categories: [activeCat] } : null)} categories={categories} onDone={load} />
      <ImportDialog open={openImport} onOpenChange={setOpenImport} categories={categories} defaultCategory={activeCat} onDone={load} />
      <AddCategoryDialog open={openAddCat || !!renamingCat} onOpenChange={(v) => { if (!v) { setOpenAddCat(false); setRenamingCat(null); } }}
        editing={renamingCat}
        onAdded={(cat) => { loadCategories(); if (!renamingCat) setActiveCat(cat.name); else { if (activeCat === renamingCat.name) setActiveCat(cat.name); load(); } }} />
    </div>
  );
}
